import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TrainingLinkPicker, trainingError } from '../components/training/TrainingLinkPicker';
import { TrainingDurationPanel } from '../components/training/TrainingDurationPanel';
import { TrainingMinutesCell } from '../components/training/TrainingMinutesCell';
import '../components/training/training-table.css';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { useSport } from '../context/SportContext';
import { 
  MapPin, 
  Activity, 
  Users, 
  Calendar,
  Plus,
  Upload,
  AlertTriangle,
  CheckCircle,
  Clock,
  ChevronRight,
  Settings,
  TrendingUp,
  Zap,
  ShieldAlert,
  Trash2,
  Loader2
} from 'lucide-react';

const GPS_METRICS_DISPLAY = {
  distance_m: "Distance (m)",
  distance_per_min: "Dist/min (m/min)",
  shi_distance: "SHI Distance (>16 km/h)",
  s7_distance: "S7 Distance (>25 km/h)",
  acchi_distance: "AccHI Distance (>3 m/s²)",
  dechi_distance: "DecHI Distance (< -3 m/s²)",
  speed_max: "Speed Max (km/h)",
  session_minutes: "Session Minutes",
  player_load: "Player Load",
  running_symmetry: "Running Symmetry",
  imbalance: "Imbalance",
};

const EVENT_TYPES = [
  { value: 'TRAINING', label: 'Training' },
  { value: 'MATCH', label: 'Match' },
  { value: 'FRIENDLY_MATCH', label: 'Friendly Match' },
  { value: 'RTP', label: 'RTP' },
  { value: 'RECOVERY', label: 'Recovery' }
];

const StaffGPSPage = () => {
  const { api, user } = useAuth();
  const { language } = useLanguage();
  const { sport, loadDefaults } = useSport();
  const isBasketball = sport === 'basketball';
  const defaultPassiveFactor = loadDefaults?.passive_load_factor;
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all');
  const [teamSettings, setTeamSettings] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [activeTab, setActiveTab] = useState('events');
  // iter_117 — analytics range lifted here so it survives tab switches (Radix unmounts inactive TabsContent by default)
  const [analyticsRange, setAnalyticsRange] = useState('30d');
  
  // Dialog states
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [showEventDetail, setShowEventDetail] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [matchOverrides, setMatchOverrides] = useState({});
  // P0.5.13D-EXT — in-flight guard to prevent double-submit that would
  // otherwise create duplicate composite (Load + Planner) records.
  const [isCreating, setIsCreating] = useState(false);
  const trainingRequest = useRef(null);
  
  // Create event form
  const [newEvent, setNewEvent] = useState({
    team_id: '',
    date: new Date().toISOString().split('T')[0],
    event_type: 'TRAINING',
    title: '',
    notes: '',
    session_minutes: 75,
    session_minutes_edited: false,
    active_minutes: '',
    passive_load_factor: null,
    assignment_mode: 'WHOLE_TEAM',
    athlete_ids: [],
    // P0.5.11 — Planner ↔ Load match integration
    match_available_minutes: '',
    planner_link_mode: 'NONE',         // 'NONE' | 'LINK_EXISTING' | 'CREATE_NEW'
    planner_session_id: '',            // used when LINK_EXISTING
  });
  const [eligiblePlanners, setEligiblePlanners] = useState([]);

  // Athletes for the specific-athletes picker
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [athleteSearch, setAthleteSearch] = useState('');
  const [loadingTeamAthletes, setLoadingTeamAthletes] = useState(false);
  
  // RTP exclusion state
  const [rtpAthletes, setRtpAthletes] = useState([]);
  const [showRtpModal, setShowRtpModal] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  
  // CSV import form
  const [csvData, setCsvData] = useState({
    team_id: '',
    date: new Date().toISOString().split('T')[0],
    event_type: 'TRAINING',
    title: '',
  });
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvMode, setCsvMode] = useState('file'); // 'file' or 'paste'
  const [csvPasteContent, setCsvPasteContent] = useState('');
  
  const canEdit = user?.role === 'ADMIN' || user?.role === 'SNC';

  const selectCreateTeam = (teamId) => {
    setNewEvent(v => ({ ...v, team_id: teamId, athlete_ids: [], training_planner_id: '' }));
    setAthleteSearch('');
    if (!teamId) { setTeamAthletes([]); return; }
    setLoadingTeamAthletes(true);
    api.get(`/athletes?team_id=${teamId}`)
      .then(res => setTeamAthletes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeamAthletes([]))
      .finally(() => setLoadingTeamAthletes(false));
  };

  const fetchData = useCallback(async ({ background = false } = {}) => {
    try {
      if (!background) setLoading(true);
      
      // Fetch teams
      const teamsRes = await api.get('/teams');
      setTeams(teamsRes.data);
      
      // Fetch team settings
      const settingsRes = await api.get('/gps/settings/all');
      setTeamSettings(settingsRes.data);
      
      // Fetch summary
      const summaryRes = await api.get('/gps/summary');
      setSummary(summaryRes.data);
      
      // Fetch events
      let eventsUrl = '/gps/events';
      if (selectedTeam && selectedTeam !== 'all') {
        eventsUrl += `?team_id=${selectedTeam}`;
      }
      const eventsRes = await api.get(eventsUrl);
      setEvents(eventsRes.data);
      
    } catch (err) {
      console.error('Failed to fetch GPS data:', err);
      toast.error('Failed to load GPS data');
    } finally {
      if (!background) setLoading(false);
    }
  }, [api, selectedTeam]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateEvent = async () => {
    // P0.5.13D-EXT — reject re-entry while a create is in flight. This
    // is the ONLY guard against the duplicate-composite production
    // incident where users clicked Create multiple times after seeing
    // a (misleading) failure toast for a request that had actually
    // succeeded. Do NOT rely on title/date/team fuzzy matching.
    if (isCreating) return;
    if (!newEvent.team_id) {
      toast.error(language === 'IT' ? 'Seleziona una squadra' : 'Please select a team');
      return;
    }
    if (newEvent.assignment_mode === 'SPECIFIC_ATHLETES' && newEvent.athlete_ids.length === 0) {
      toast.error(language === 'IT' ? 'Seleziona almeno un atleta' : 'Select at least one athlete');
      return;
    }

    const isMatchType = newEvent.event_type === 'MATCH' || newEvent.event_type === 'FRIENDLY_MATCH';

    // P0.5.11 — MATCH / FRIENDLY_MATCH must carry an explicit
    // match_available_minutes and (optionally) a Planner link.
    const availMinutesRaw = parseInt(newEvent.match_available_minutes, 10);
    if (isMatchType) {
      if (!Number.isFinite(availMinutesRaw) || availMinutesRaw <= 0) {
        toast.error(
          language === 'IT'
            ? 'Inserisci i minuti totali disponibili della partita'
            : 'Enter total match available minutes',
        );
        return;
      }
    }

    // Composite create — Load + brand-new Planner match
    if (isMatchType && newEvent.planner_link_mode === 'CREATE_NEW') {
      setIsCreating(true);
      try {
        const compPayload = {
          team_id: newEvent.team_id,
          date: newEvent.date,
          event_type: newEvent.event_type,
          title: newEvent.title || (newEvent.event_type === 'MATCH' ? 'Match' : 'Friendly Match'),
          match_available_minutes: availMinutesRaw,
          athlete_ids: newEvent.assignment_mode === 'WHOLE_TEAM' ? null : newEvent.athlete_ids,
        };
        await api.post('/gps/events/with-planner', compPayload);
        toast.success(
          language === 'IT'
            ? 'Partita creata su Planner e Load'
            : 'Match created on Planner and Load',
        );
        // P0.5.13D — post-success handler must use the actual state names.
        // The previous references (setShowCreateModal / loadEvents) were
        // undefined and threw a ReferenceError, causing a misleading
        // "Failed to create linked match" toast for a workflow that had
        // actually succeeded on the backend.
        setShowCreateEvent(false);
        fetchData();
        return;
      } catch (err) {
        // Backend may return `detail` as a string OR as an object (e.g.
        // DURATION_MISMATCH). Render both safely so the user sees the real
        // reason instead of the generic fallback.
        const d = err?.response?.data?.detail;
        const msg = typeof d === 'string'
          ? d
          : (d && typeof d === 'object' && d.message) || null;
        toast.error(msg || (language === 'IT'
          ? 'Creazione partita collegata fallita'
          : 'Failed to create linked match'));
        return;
      } finally {
        setIsCreating(false);
      }
    }

    const isWhole = newEvent.assignment_mode === 'WHOLE_TEAM';
    const payload = {
      team_id: newEvent.team_id,
      date: newEvent.date,
      event_type: newEvent.event_type,
      title: newEvent.title || null,
      notes: newEvent.notes || null,
      session_minutes: isMatchType || newEvent.session_minutes === '' ? null : Number(newEvent.session_minutes),
      athlete_ids: isWhole ? null : newEvent.athlete_ids,
      exclude_rtp: false
    };
    if (isMatchType) {
      payload.match_available_minutes = availMinutesRaw;
      if (newEvent.planner_link_mode === 'LINK_EXISTING' && newEvent.planner_session_id) {
        payload.planner_session_id = newEvent.planner_session_id;
      }
    }
    // Basketball load fields: only persist when org sport=basketball AND event type is not a match.
    if (isBasketball && !isMatchType) {
      const act = parseInt(newEvent.active_minutes);
      const dur = payload.session_minutes;
      if (!isNaN(act)) {
        if (act <= 0) return toast.error('Active minutes must be > 0');
        if (dur && act > dur) return toast.error('Active minutes cannot exceed total duration');
        payload.active_minutes = act;
      }
      const factor = parseFloat(newEvent.passive_load_factor ?? defaultPassiveFactor);
      if (!isNaN(factor) && factor >= 0 && factor <= 1) {
        payload.passive_load_factor = factor;
      }
    }

    if (newEvent.event_type === 'TRAINING') {
      if (newEvent.training_link_mode === 'LINK_EXISTING') {
        if (!newEvent.training_planner_id) return toast.error(language === 'IT' ? 'Seleziona una sessione TRAINING' : 'Select a TRAINING session');
        payload.planner_session_id = newEvent.training_planner_id;
      }
      if (newEvent.training_link_mode === 'CREATE_NEW') {
        const raw = newEvent.training_planner_duration ?? newEvent.session_minutes;
        if (raw === '' || !Number.isSafeInteger(Number(raw)) || Number(raw) < 0) return toast.error(language === 'IT' ? 'Durata Planner non valida' : 'Invalid Planner duration');
        payload.planner_duration_min = Number(raw);
        payload.title = newEvent.title.trim() || 'Training';
      }
    }

    // If assigning whole team, check for RTP athletes
    if (isWhole) {
      try {
        const res = await api.get(`/gps/team/${newEvent.team_id}/rtp-athletes`);
        if (res.data.rtp_athletes && res.data.rtp_athletes.length > 0) {
          setRtpAthletes(res.data.rtp_athletes);
          setPendingPayload(payload);
          setShowRtpModal(true);
          return;
        }
      } catch (err) {
        // If RTP check fails, proceed without exclusion
        console.warn('RTP check failed:', err);
      }
    }

    await submitCreateEvent(payload);
  };

  const submitCreateEvent = async (payload) => {
    // P0.5.13D-EXT — in-flight guard shared with composite path.
    setIsCreating(true);
    try {
      let path = '/gps/events';
      if (payload.event_type === 'TRAINING') {
        const signature = JSON.stringify(payload);
        if (trainingRequest.current?.signature !== signature) trainingRequest.current = { signature, key: crypto.randomUUID() };
        payload = { ...payload, idempotency_key: trainingRequest.current.key };
        if (payload.planner_duration_min != null) path = '/gps/training/with-planner';
      }
      const created = await api.post(path, payload);
      trainingRequest.current = null;
      toast.success(language === 'IT' ? 'Evento creato' : 'Event created successfully');
      setShowCreateEvent(false);
      setShowRtpModal(false);
      setPendingPayload(null);
      setRtpAthletes([]);
      setNewEvent({
        team_id: '',
        date: new Date().toISOString().split('T')[0],
        event_type: 'TRAINING',
        title: '',
        notes: '',
        session_minutes: 75,
        session_minutes_edited: false,
        active_minutes: '',
        passive_load_factor: null,
        assignment_mode: 'WHOLE_TEAM',
        athlete_ids: []
      });
      setTeamAthletes([]);
      setAthleteSearch('');
      fetchData();
      if (payload.event_type === 'TRAINING' && created.data.planner_session_id) setShowEventDetail(created.data);
    } catch (err) {
      toast.error(trainingError(err, language));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRtpDecision = (decision) => {
    if (decision === 'cancel') {
      setShowRtpModal(false);
      setPendingPayload(null);
      return;
    }
    const payload = { ...pendingPayload, exclude_rtp: decision === 'exclude' };
    submitCreateEvent(payload);
  };

  const handleCSVPreview = async () => {
    if (!csvData.team_id) { toast.error('Select a team'); return; }
    if (csvMode === 'file' && !csvFile) { toast.error('Select a CSV file'); return; }
    if (csvMode === 'paste' && !csvPasteContent.trim()) { toast.error('Paste CSV data'); return; }
    setCsvImporting(true);
    try {
      const formData = new FormData();
      if (csvMode === 'file') {
        formData.append('file', csvFile);
      } else {
        const blob = new Blob([csvPasteContent], { type: 'text/csv' });
        formData.append('file', blob, 'pasted.csv');
      }
      const res = await api.post(`/gps/events/parse-csv?team_id=${csvData.team_id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setCsvPreview(res.data);
      setMatchOverrides({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to parse CSV');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleCSVImport = async () => {
    if (!csvData.team_id) return;
    setCsvImporting(true);
    try {
      const formData = new FormData();
      if (csvMode === 'file') {
        formData.append('file', csvFile);
      } else {
        const blob = new Blob([csvPasteContent], { type: 'text/csv' });
        formData.append('file', blob, 'pasted.csv');
      }
      const params = new URLSearchParams({
        team_id: csvData.team_id,
        date: csvData.date,
        event_type: csvData.event_type,
        title: csvData.title || '',
      });
      // Include manual match overrides (suggested/unmatched rows that user confirmed)
      if (Object.keys(matchOverrides).length > 0) {
        params.set('match_overrides', JSON.stringify(matchOverrides));
      }
      const res = await api.post(`/gps/events/import-csv?${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { rows_imported, rows_failed, unmatched_rows } = res.data;
      if (rows_imported > 0) {
        toast.success(`Imported ${rows_imported} athletes${rows_failed > 0 ? `, ${rows_failed} unmatched` : ''}`);
      }
      if (unmatched_rows?.length > 0) {
        toast.warning(`Unmatched: ${unmatched_rows.join(', ')}`);
      }
      setShowImportCSV(false);
      setCsvPreview(null);
      setCsvFile(null);
      setCsvPasteContent('');
      setCsvData({ team_id: '', date: new Date().toISOString().split('T')[0], event_type: 'TRAINING', title: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setCsvImporting(false);
    }
  };

  const handleUpdateTeamMode = async (teamId, mode) => {
    try {
      await api.put(`/gps/settings/team/${teamId}`, { load_mode: mode });
      toast.success('Team settings updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const getStatusBadge = (status) => {
    const safeStatus = status || 'CREATED';
    const styles = {
      COMPLETED: 'bg-emerald-500/20 text-emerald-400',
      PARTIAL_RPE: 'bg-amber-500/20 text-amber-400',
      AWAITING_RPE: 'bg-blue-500/20 text-blue-400',
      CREATED: 'bg-zinc-700/50 text-zinc-400'
    };
    const icons = {
      COMPLETED: <CheckCircle className="w-3 h-3" />,
      PARTIAL_RPE: <AlertTriangle className="w-3 h-3" />,
      AWAITING_RPE: <Clock className="w-3 h-3" />,
      CREATED: <Clock className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${styles[safeStatus] || styles.CREATED}`}>
        {icons[safeStatus] || icons.CREATED}
        {safeStatus.replace('_', ' ')}
      </span>
    );
  };

  const getSourceBadge = (source) => {
    if (source === 'GPS_IMPORT') {
      return <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">GPS</span>;
    }
    return <span className="text-xs px-2 py-0.5 bg-zinc-700/50 text-zinc-400 rounded">Manual</span>;
  };

  if (loading) {
    return (
      <div className="space-y-6 fade-in">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {language === 'IT' ? 'GPS / Carico' : 'GPS / Load'}
          </h1>
        </div>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-8 text-center text-zinc-500">
            Loading...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-6 fade-in" data-testid="staff-gps-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">
            {language === 'IT' ? 'GPS / Carico' : 'GPS / Load'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {language === 'IT' ? 'Monitoraggio carichi di lavoro' : 'Training load monitoring'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <>
              <Button
                variant="outline"
                onClick={() => setShowSettings(true)}
                className="border-zinc-700 text-zinc-300"
                data-testid="gps-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Impostazioni' : 'Settings'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowImportCSV(true)}
                className="border-zinc-700 text-zinc-300"
                data-testid="gps-import-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Importa CSV' : 'Import CSV'}
              </Button>
              <Button
                onClick={() => setShowCreateEvent(true)}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                data-testid="gps-create-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                {language === 'IT' ? 'Nuovo Evento' : 'New Event'}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-sm flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-100" data-testid="total-events">{summary.total_events}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Eventi (7gg)' : 'Events (7d)'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-sm flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-amber-400" data-testid="rpe-missing">{summary.rpe_missing}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'RPE Mancanti' : 'RPE Missing'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-sm flex items-center justify-center">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-100" data-testid="total-load">{Math.round(summary.total_training_load)}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Carico Totale' : 'Total Load'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-sm flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <div className="text-2xl font-bold text-zinc-100" data-testid="avg-load">{Math.round(summary.avg_training_load)}</div>
                  <div className="text-xs text-zinc-500">{language === 'IT' ? 'Media Carico' : 'Avg Load'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Filter */}
      <div className="flex items-center gap-4">
        <Select value={selectedTeam} onValueChange={setSelectedTeam}>
          <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800" data-testid="team-filter">
            <SelectValue placeholder={language === 'IT' ? 'Tutti i team' : 'All teams'} />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">{language === 'IT' ? 'Tutti i team' : 'All teams'}</SelectItem>
            {teams.map(team => (
              <SelectItem key={team.id} value={team.id}>{team.code} - {team.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="text-sm text-zinc-500">
          {events.length} {language === 'IT' ? 'eventi' : 'events'}
        </span>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-900/50 border border-zinc-800">
          <TabsTrigger value="events" className="data-[state=active]:bg-zinc-800">
            {language === 'IT' ? 'Eventi' : 'Events'}
          </TabsTrigger>
          <TabsTrigger value="analytics" className="data-[state=active]:bg-zinc-800">
            {language === 'IT' ? 'Analisi' : 'Analytics'}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="events" className="mt-4">
          {/* Events Table */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Data' : 'Date'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Squadra' : 'Team'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Tipo' : 'Type'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Titolo' : 'Title'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Fonte' : 'Source'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Atleti' : 'Athletes'}</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">RPE</th>
                      <th className="text-left p-4 text-xs font-semibold text-zinc-500 uppercase tracking-wider">{language === 'IT' ? 'Stato' : 'Status'}</th>
                      <th className="text-left p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.length === 0 ? (
                      <tr>
                        <td colSpan="9" className="p-8 text-center text-zinc-500">
                          {language === 'IT' ? 'Nessun evento trovato' : 'No events found'}
                        </td>
                      </tr>
                    ) : (
                      events.map(event => (
                        <tr 
                          key={event.id} 
                          className="border-b border-zinc-800/50 hover:bg-zinc-800/30 cursor-pointer transition-colors"
                          onClick={() => setShowEventDetail(event)}
                          data-testid={`event-row-${event.id}`}
                        >
                          <td className="p-4 text-zinc-100 font-mono text-sm">{event.date}</td>
                          <td className="p-4">
                            <span className="text-amber-500 font-semibold">{event.team_code}</span>
                          </td>
                          <td className="p-4 text-zinc-300 text-sm">{event.event_type.replace('_', ' ')}</td>
                          <td className="p-4 text-zinc-400 text-sm max-w-[200px] truncate">{event.title}</td>
                          <td className="p-4">{getSourceBadge(event.source)}</td>
                          <td className="p-4 text-zinc-300">{event.athlete_count}</td>
                          <td className="p-4">
                            <span className="text-emerald-400">{event.rpe_submitted_count}</span>
                            <span className="text-zinc-600">/</span>
                            <span className="text-zinc-400">{event.athlete_count}</span>
                            {event.rpe_missing_count > 0 && (
                              <span className="ml-2 text-amber-400 text-xs">({event.rpe_missing_count} missing)</span>
                            )}
                          </td>
                          <td className="p-4">{getStatusBadge(event.status)}</td>
                          <td className="p-4">
                            <ChevronRight className="w-4 h-4 text-zinc-600" />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          <AnalyticsPanel
            selectedTeam={selectedTeam}
            teams={teams}
            api={api}
            language={language}
            range={analyticsRange}
            setRange={setAnalyticsRange}
          />
        </TabsContent>
      </Tabs>

      {/* Create Event Dialog */}
      <Dialog open={showCreateEvent} onOpenChange={setShowCreateEvent}>
        <DialogContent
          className="bg-zinc-900 border-zinc-800 w-[95vw] sm:max-w-lg p-0 gap-0 flex flex-col max-h-[95dvh] max-h-[95vh] overflow-hidden"
          data-testid="create-event-dialog"
          aria-describedby={undefined}
        >
          <DialogHeader className="flex-shrink-0 px-4 py-3 sm:px-6 sm:py-4 border-b border-zinc-800">
            <DialogTitle className="text-zinc-100 flex items-center gap-2 text-base sm:text-lg">
              <Plus className="w-5 h-5 text-amber-500" />
              {language === 'IT' ? 'Crea Evento Manuale' : 'Create Manual Event'}
            </DialogTitle>
          </DialogHeader>
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 sm:px-6 py-4 space-y-4"
            style={{ WebkitOverflowScrolling: 'touch' }}
            data-testid="create-event-body"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Squadra' : 'Team'} *</Label>
                {newEvent.event_type === 'TRAINING' ? <select value={newEvent.team_id} onChange={e => selectCreateTeam(e.target.value)}
                  aria-label={language === 'IT' ? 'Squadra' : 'Team'} data-testid="create-team-select"
                  className="w-full min-w-0 rounded-md bg-zinc-950 border border-zinc-800 px-3 py-2 text-sm text-zinc-100">
                  <option value="">{language === 'IT' ? 'Seleziona squadra' : 'Select team'}</option>
                  {teams.map(team => <option key={team.id} value={team.id} data-testid={`training-create-team-${team.id}`}>{`${team.code} - ${team.name}`}</option>)}
                </select> : <Select value={newEvent.team_id} onValueChange={selectCreateTeam}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="create-team-select">
                    <SelectValue placeholder={language === 'IT' ? 'Seleziona squadra' : 'Select team'} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {teams.map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.code} - {team.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>}
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Data' : 'Date'} *</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({...newEvent, date: e.target.value, training_planner_id: ''})}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="create-date-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-400">{language === 'IT' ? 'Tipo Evento' : 'Event Type'}</Label>
                <Select value={newEvent.event_type} onValueChange={(v) => setNewEvent({...newEvent, event_type: v, training_link_mode: 'NONE', training_planner_id: ''})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="create-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {EVENT_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {newEvent.event_type !== 'MATCH' && newEvent.event_type !== 'FRIENDLY_MATCH' && (
                <div className="space-y-2">
                  <Label className="text-zinc-400">{newEvent.event_type === 'TRAINING' && newEvent.training_link_mode && newEvent.training_link_mode !== 'NONE'
                    ? (language === 'IT' ? 'Minuti per calcolo carico' : 'Minutes for load calculation')
                    : (language === 'IT' ? 'Durata (min)' : 'Duration (min)')}</Label>
                  <Input
                    type="number"
                    value={newEvent.session_minutes}
                    onChange={(e) => setNewEvent({...newEvent, session_minutes: e.target.value, session_minutes_edited: true})}
                    className="bg-zinc-950 border-zinc-800"
                    min={newEvent.event_type === 'TRAINING' ? '0' : '1'}
                    max="300"
                    data-testid="create-duration-input"
                  />
                </div>
              )}
              {(newEvent.event_type === 'MATCH' || newEvent.event_type === 'FRIENDLY_MATCH') && (
                <div className="space-y-2">
                  <Label className="text-zinc-400" htmlFor="match-avail-min">
                    {language === 'IT' ? 'Minuti totali disponibili *' : 'Match available minutes *'}
                  </Label>
                  <Input
                    id="match-avail-min"
                    type="number"
                    inputMode="numeric"
                    value={newEvent.match_available_minutes}
                    onChange={(e) => setNewEvent({...newEvent, match_available_minutes: e.target.value})}
                    className="bg-zinc-950 border-zinc-800"
                    min="1"
                    max="200"
                    placeholder={language === 'IT' ? 'es. 90' : 'e.g. 90'}
                    data-testid="create-match-available-minutes-input"
                  />
                  <p className="text-[10px] text-zinc-500">
                    {language === 'IT'
                      ? 'Durata ufficiale della partita. Non è il minutaggio del singolo giocatore.'
                      : 'Official match duration. This is NOT per-player minutes.'}
                  </p>
                </div>
              )}
            </div>

            {newEvent.event_type === 'TRAINING' && <TrainingLinkPicker api={api} language={language}
              teamId={newEvent.team_id} date={newEvent.date} value={newEvent} disabled={isCreating}
              onChange={patch => setNewEvent(v => {
                const next = { ...v, ...patch };
                if (!v.session_minutes_edited) {
                  if (patch.training_planner_id && patch.training_selected_duration != null) next.session_minutes = patch.training_selected_duration;
                  if (Object.prototype.hasOwnProperty.call(patch, 'training_planner_duration')) next.session_minutes = patch.training_planner_duration;
                }
                return next;
              })} prefix="training-create-link" />}

            {/* P0.5.11 — Planner match integration for MATCH / FRIENDLY_MATCH */}
            {(newEvent.event_type === 'MATCH' || newEvent.event_type === 'FRIENDLY_MATCH') && (
              <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 space-y-3" data-testid="planner-link-section">
                <Label className="text-blue-300 text-sm font-semibold">
                  {language === 'IT' ? 'Partita nel Planner' : 'Planner Match'}
                </Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={() => setNewEvent({...newEvent, planner_link_mode: 'NONE', planner_session_id: ''})}
                    className={`px-3 py-2 text-xs rounded border ${
                      newEvent.planner_link_mode === 'NONE'
                        ? 'bg-blue-500/20 border-blue-400 text-blue-100'
                        : 'border-zinc-700 text-zinc-400'
                    }`}
                    data-testid="planner-link-none-btn"
                  >
                    {language === 'IT' ? 'Nessun collegamento' : 'No link'}
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      setNewEvent({...newEvent, planner_link_mode: 'LINK_EXISTING', planner_session_id: ''});
                      if (newEvent.team_id) {
                        try {
                          const df = new Date(newEvent.date); df.setDate(df.getDate() - 30);
                          const dt = new Date(newEvent.date); dt.setDate(dt.getDate() + 30);
                          const r = await api.get('/planner/match-sessions/eligible', {
                            params: {
                              team_id: newEvent.team_id,
                              event_type: newEvent.event_type,
                              date_from: df.toISOString().split('T')[0],
                              date_to: dt.toISOString().split('T')[0],
                            },
                          });
                          setEligiblePlanners(r.data || []);
                        } catch (err) {
                          setEligiblePlanners([]);
                        }
                      }
                    }}
                    className={`px-3 py-2 text-xs rounded border ${
                      newEvent.planner_link_mode === 'LINK_EXISTING'
                        ? 'bg-blue-500/20 border-blue-400 text-blue-100'
                        : 'border-zinc-700 text-zinc-400'
                    }`}
                    data-testid="planner-link-existing-btn"
                  >
                    {language === 'IT' ? 'Collega partita esistente' : 'Link existing Planner match'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewEvent({...newEvent, planner_link_mode: 'CREATE_NEW', planner_session_id: ''})}
                    className={`px-3 py-2 text-xs rounded border ${
                      newEvent.planner_link_mode === 'CREATE_NEW'
                        ? 'bg-blue-500/20 border-blue-400 text-blue-100'
                        : 'border-zinc-700 text-zinc-400'
                    }`}
                    data-testid="planner-link-create-btn"
                  >
                    {language === 'IT' ? 'Crea nuova partita nel Planner' : 'Create new Planner match'}
                  </button>
                </div>
                {newEvent.planner_link_mode === 'LINK_EXISTING' && (
                  <div className="space-y-1">
                    <Label className="text-zinc-400 text-xs">
                      {language === 'IT' ? 'Partite disponibili' : 'Available matches'}
                    </Label>
                    {eligiblePlanners.length === 0 ? (
                      <p className="text-xs text-zinc-500 italic" data-testid="no-eligible-planner-matches">
                        {language === 'IT'
                          ? 'Nessuna partita Planner compatibile in questa finestra.'
                          : 'No compatible Planner match in this window.'}
                      </p>
                    ) : (
                      <Select
                        value={newEvent.planner_session_id}
                        onValueChange={(v) => setNewEvent({...newEvent, planner_session_id: v})}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="planner-select-trigger">
                          <SelectValue placeholder={language === 'IT' ? 'Seleziona…' : 'Select…'} />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          {eligiblePlanners.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.date} · {p.title || (newEvent.event_type === 'MATCH' ? 'Match' : 'Friendly')}
                              {p.duration_planned_min ? ` · ${p.duration_planned_min}m` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-zinc-500">
                  {language === 'IT'
                    ? 'I minuti per singolo giocatore vengono inseriti nel dettaglio evento.'
                    : 'Per-player minutes are entered in the event detail.'}
                </p>
              </div>
            )}
            {/* end P0.5.11 Planner Match integration */}

            {/* Basketball Active/Passive Load Settings — only for basketball orgs, non-match events. */}
            {isBasketball && newEvent.event_type !== 'MATCH' && newEvent.event_type !== 'FRIENDLY_MATCH' && (() => {
              const dur = parseInt(newEvent.session_minutes) || 0;
              const act = parseInt(newEvent.active_minutes) || 0;
              const factor = parseFloat(newEvent.passive_load_factor ?? defaultPassiveFactor);
              const validFactor = (!isNaN(factor) && factor >= 0 && factor <= 1) ? factor : defaultPassiveFactor;
              const passive = Math.max(0, dur - act);
              const adjusted = act > 0 && validFactor != null ? Math.round((act + passive * validFactor) * 10) / 10 : null;
              const previewLoad = adjusted != null ? Math.round(adjusted * 6 * 10) / 10 : null;
              const invalid = act > 0 && (act > dur);
              return (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 space-y-3" data-testid="basketball-load-settings">
                  <div className="flex items-center justify-between">
                    <Label className="text-amber-300 text-sm font-semibold">
                      🏀 {language === 'IT' ? 'Carico Basket (Attivo / Passivo)' : 'Basketball Load Settings (Active / Passive)'}
                    </Label>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Minuti Attivi *' : 'Active Minutes *'}</Label>
                      <Input
                        type="number"
                        value={newEvent.active_minutes}
                        onChange={(e) => setNewEvent({ ...newEvent, active_minutes: e.target.value })}
                        className="bg-zinc-950 border-zinc-800"
                        min="1"
                        max={dur || 300}
                        placeholder="e.g. 45"
                        data-testid="create-active-minutes-input"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Fattore Carico Passivo' : 'Passive Load Factor'} (0–1)</Label>
                      <Input
                        type="number"
                        step="0.05"
                        value={newEvent.passive_load_factor ?? defaultPassiveFactor ?? ''}
                        onChange={(e) => setNewEvent({ ...newEvent, passive_load_factor: e.target.value })}
                        className="bg-zinc-950 border-zinc-800"
                        min="0"
                        max="1"
                        data-testid="create-passive-factor-input"
                      />
                    </div>
                  </div>
                  {act > 0 && (
                    <div className="grid grid-cols-3 gap-2 text-xs pt-2 border-t border-amber-500/20" data-testid="basketball-load-preview">
                      <div>
                        <div className="text-zinc-500 text-[10px] uppercase">{language === 'IT' ? 'Min Passivi' : 'Passive Min'}</div>
                        <div className="text-zinc-200 font-semibold" data-testid="passive-minutes-preview">{passive}</div>
                      </div>
                      <div>
                        <div className="text-zinc-500 text-[10px] uppercase">{language === 'IT' ? 'Min Aggiustati' : 'Adjusted Min'}</div>
                        <div className="text-amber-300 font-semibold" data-testid="adjusted-minutes-preview">{adjusted ?? '—'}</div>
                      </div>
                      <div>
                        <div className="text-zinc-500 text-[10px] uppercase">{language === 'IT' ? 'Es. Carico (RPE 6)' : 'Est. Load (RPE 6)'}</div>
                        <div className="text-amber-300 font-semibold" data-testid="example-load-preview">{previewLoad ?? '—'} AU</div>
                      </div>
                    </div>
                  )}
                  {invalid && (
                    <p className="text-[11px] text-rose-400" data-testid="basketball-validation-error">
                      {language === 'IT' ? 'I minuti attivi non possono superare la durata totale.' : 'Active minutes cannot exceed total duration.'}
                    </p>
                  )}
                  <p className="text-[10px] text-zinc-500">
                    {language === 'IT'
                      ? 'Solo basket. Carico finale = RPE × Min Aggiustati.'
                      : 'Basketball only. Final load = RPE × Adjusted Minutes.'}
                  </p>
                </div>
              );
            })()}
            
            <div className="space-y-2">
              <Label className="text-zinc-400">{language === 'IT' ? 'Titolo (opzionale)' : 'Title (optional)'}</Label>
              <Input
                value={newEvent.title}
                onChange={(e) => setNewEvent({...newEvent, title: e.target.value})}
                placeholder={language === 'IT' ? 'es: Allenamento mattina' : 'e.g., Morning Training'}
                className="bg-zinc-950 border-zinc-800"
                data-testid="create-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-zinc-400">{language === 'IT' ? 'Note (opzionale)' : 'Notes (optional)'}</Label>
              <Textarea
                value={newEvent.notes}
                onChange={(e) => setNewEvent({...newEvent, notes: e.target.value})}
                className="bg-zinc-950 border-zinc-800"
                rows={2}
              />
            </div>
            
            <div className="p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm space-y-3" data-testid="assignment-mode-block">
              <Label className="text-zinc-300 text-sm">
                {language === 'IT' ? 'Modalità assegnazione' : 'Assignment mode'}
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setNewEvent({...newEvent, assignment_mode: 'WHOLE_TEAM', athlete_ids: []})}
                  className={`px-3 py-2 min-h-[44px] rounded-sm text-sm font-medium border transition-colors ${
                    newEvent.assignment_mode === 'WHOLE_TEAM'
                      ? 'bg-amber-500 text-zinc-950 border-amber-500'
                      : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-zinc-500'
                  }`}
                  data-testid="mode-whole-team"
                >
                  {language === 'IT' ? 'Tutta la squadra' : 'Whole team'}
                </button>
                <button
                  type="button"
                  onClick={() => setNewEvent({...newEvent, assignment_mode: 'SPECIFIC_ATHLETES'})}
                  className={`px-3 py-2 min-h-[44px] rounded-sm text-sm font-medium border transition-colors ${
                    newEvent.assignment_mode === 'SPECIFIC_ATHLETES'
                      ? 'bg-amber-500 text-zinc-950 border-amber-500'
                      : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-zinc-500'
                  }`}
                  data-testid="mode-specific-athletes"
                >
                  {language === 'IT' ? 'Atleti specifici' : 'Specific athletes'}
                </button>
              </div>

              {newEvent.assignment_mode === 'WHOLE_TEAM' && (
                <p className="text-xs text-zinc-400" data-testid="whole-team-count">
                  {newEvent.team_id
                    ? (language === 'IT'
                        ? `${teamAthletes.length} atleti saranno assegnati`
                        : `${teamAthletes.length} athletes will be assigned`)
                    : (language === 'IT' ? 'Seleziona prima una squadra' : 'Select a team first')}
                </p>
              )}

              {newEvent.assignment_mode === 'SPECIFIC_ATHLETES' && (
                <div className="space-y-2" data-testid="specific-athletes-picker">
                  {!newEvent.team_id ? (
                    <p className="text-xs text-zinc-500 italic" data-testid="picker-no-team">
                      {language === 'IT' ? 'Seleziona prima una squadra' : 'Select a team first'}
                    </p>
                  ) : (
                    <>
                      <Input
                        placeholder={language === 'IT' ? 'Cerca atleta...' : 'Search athlete...'}
                        value={athleteSearch}
                        onChange={(e) => setAthleteSearch(e.target.value)}
                        className="bg-zinc-900 border-zinc-700 h-9 text-sm"
                        data-testid="athlete-search-input"
                      />
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-zinc-400" data-testid="selected-count">
                          {language === 'IT'
                            ? `${newEvent.athlete_ids.length} atleti selezionati`
                            : `${newEvent.athlete_ids.length} athletes selected`}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              const filtered = teamAthletes
                                .filter((a) => {
                                  const q = athleteSearch.toLowerCase();
                                  if (!q) return true;
                                  return `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
                                })
                                .map((a) => a.id);
                              setNewEvent({...newEvent, athlete_ids: Array.from(new Set([...newEvent.athlete_ids, ...filtered]))});
                            }}
                            className="text-amber-400 hover:text-amber-300"
                            data-testid="select-all-athletes"
                          >
                            {language === 'IT' ? 'Seleziona tutti' : 'Select all'}
                          </button>
                          <span className="text-zinc-600">·</span>
                          <button
                            type="button"
                            onClick={() => setNewEvent({...newEvent, athlete_ids: []})}
                            className="text-zinc-400 hover:text-zinc-200"
                            data-testid="clear-athlete-selection"
                          >
                            {language === 'IT' ? 'Deseleziona tutti' : 'Clear'}
                          </button>
                        </div>
                      </div>
                      <div className="max-h-40 sm:max-h-56 overflow-y-auto overscroll-contain border border-zinc-800 rounded-sm divide-y divide-zinc-800" style={{ WebkitOverflowScrolling: 'touch' }} data-testid="athlete-picker-list">
                        {loadingTeamAthletes && (
                          <p className="p-3 text-xs text-zinc-500 italic">
                            {language === 'IT' ? 'Caricamento...' : 'Loading...'}
                          </p>
                        )}
                        {!loadingTeamAthletes && teamAthletes.length === 0 && (
                          <p className="p-3 text-xs text-zinc-500 italic">
                            {language === 'IT' ? 'Nessun atleta trovato' : 'No athletes found'}
                          </p>
                        )}
                        {!loadingTeamAthletes && teamAthletes
                          .filter((a) => {
                            const q = athleteSearch.toLowerCase();
                            if (!q) return true;
                            return `${a.first_name} ${a.last_name}`.toLowerCase().includes(q);
                          })
                          .map((a) => {
                            const selected = newEvent.athlete_ids.includes(a.id);
                            return (
                              <label
                                key={a.id}
                                className={`flex items-center gap-2 px-3 py-2 min-h-[44px] cursor-pointer hover:bg-zinc-900 ${selected ? 'bg-amber-500/10' : ''}`}
                                data-testid={`athlete-picker-row-${a.id}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selected}
                                  onChange={(e) => {
                                    const next = e.target.checked
                                      ? Array.from(new Set([...newEvent.athlete_ids, a.id]))
                                      : newEvent.athlete_ids.filter((x) => x !== a.id);
                                    setNewEvent({...newEvent, athlete_ids: next});
                                  }}
                                  className="rounded border-zinc-700"
                                  data-testid={`athlete-picker-checkbox-${a.id}`}
                                />
                                <span className="text-sm text-zinc-200 flex-1">
                                  {a.first_name} {a.last_name}
                                </span>
                                {a.position && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                                    {a.position}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                      </div>
                      {newEvent.athlete_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1.5" data-testid="selected-athletes-chips">
                          {newEvent.athlete_ids.map((aid) => {
                            const a = teamAthletes.find((x) => x.id === aid);
                            if (!a) return null;
                            return (
                              <span
                                key={aid}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] bg-amber-500/20 text-amber-300 rounded"
                                data-testid={`chip-${aid}`}
                              >
                                {a.first_name} {a.last_name}
                                <button
                                  type="button"
                                  onClick={() => setNewEvent({...newEvent, athlete_ids: newEvent.athlete_ids.filter((x) => x !== aid)})}
                                  className="ml-0.5 text-amber-300 hover:text-white"
                                  data-testid={`chip-remove-${aid}`}
                                  aria-label="Remove"
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <DialogFooter
            className="flex-shrink-0 flex flex-col-reverse sm:flex-row sm:justify-end gap-2 px-4 py-3 sm:px-6 sm:py-4 border-t border-zinc-800 bg-zinc-900"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            data-testid="create-event-footer"
          >
            <Button
              variant="outline"
              onClick={() => setShowCreateEvent(false)}
              className="border-zinc-700 w-full sm:w-auto min-h-[44px]"
              data-testid="create-event-cancel"
            >
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button
              onClick={handleCreateEvent}
              disabled={isCreating}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400 w-full sm:w-auto min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="create-event-submit"
            >
              {isCreating
                ? (language === 'IT' ? 'Creazione…' : 'Creating…')
                : (language === 'IT' ? 'Crea Evento' : 'Create Event')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={showImportCSV} onOpenChange={(open) => { setShowImportCSV(open); if (!open) { setCsvPreview(null); setCsvFile(null); setCsvPasteContent(''); } }}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Upload className="w-5 h-5 text-blue-400" />
              {language === 'IT' ? 'Importa Dati GPS' : 'Import GPS Data'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Row 1: Team, Date, Type, Title */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Squadra' : 'Team'} *</Label>
                <Select value={csvData.team_id} onValueChange={(v) => { setCsvData({...csvData, team_id: v}); setCsvPreview(null); }}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8" data-testid="import-team-select">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {teams.filter(t => teamSettings.find(s => s.team_id === t.id && s.load_mode === 'GPS')).map(team => (
                      <SelectItem key={team.id} value={team.id}>{team.code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Data' : 'Date'} *</Label>
                <Input type="date" value={csvData.date} onChange={(e) => setCsvData({...csvData, date: e.target.value})} className="bg-zinc-950 border-zinc-800 h-8" />
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Tipo' : 'Type'}</Label>
                <Select value={csvData.event_type} onValueChange={(v) => setCsvData({...csvData, event_type: v})}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 h-8"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-zinc-400 text-xs">{language === 'IT' ? 'Titolo' : 'Title'}</Label>
                <Input value={csvData.title} onChange={(e) => setCsvData({...csvData, title: e.target.value})} placeholder="Optional" className="bg-zinc-950 border-zinc-800 h-8" />
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex items-center gap-4 border-b border-zinc-800 pb-2">
              <button onClick={() => { setCsvMode('file'); setCsvPreview(null); }} className={`text-sm px-3 py-1 rounded-sm ${csvMode === 'file' ? 'bg-blue-500/20 text-blue-400' : 'text-zinc-500 hover:text-zinc-300'}`} data-testid="csv-mode-file">
                {language === 'IT' ? 'Upload File' : 'Upload File'}
              </button>
              <button onClick={() => { setCsvMode('paste'); setCsvPreview(null); }} className={`text-sm px-3 py-1 rounded-sm ${csvMode === 'paste' ? 'bg-blue-500/20 text-blue-400' : 'text-zinc-500 hover:text-zinc-300'}`} data-testid="csv-mode-paste">
                {language === 'IT' ? 'Incolla CSV' : 'Paste CSV'}
              </button>
            </div>

            {/* File input or paste */}
            {csvMode === 'file' ? (
              <div className="space-y-2">
                <div className="border-2 border-dashed border-zinc-700 rounded-sm p-6 text-center hover:border-blue-500/50 transition-colors">
                  <input
                    type="file"
                    accept=".csv,.txt"
                    onChange={(e) => { setCsvFile(e.target.files[0]); setCsvPreview(null); }}
                    className="hidden"
                    id="csv-file-input"
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-file-input" className="cursor-pointer">
                    <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-2" />
                    <div className="text-sm text-zinc-400">
                      {csvFile ? csvFile.name : (language === 'IT' ? 'Seleziona file CSV (.csv)' : 'Select CSV file (.csv)')}
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{language === 'IT' ? 'Separatore: punto e virgola (;) o virgola (,)' : 'Separator: semicolon (;) or comma (,)'}</div>
                  </label>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                <Textarea
                  value={csvPasteContent}
                  onChange={(e) => { setCsvPasteContent(e.target.value); setCsvPreview(null); }}
                  placeholder="Player;T;D;Drel;D_SHI;D_S7..."
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs"
                  rows={5}
                  data-testid="import-csv-content"
                />
              </div>
            )}

            {/* Preview button */}
            {!csvPreview && (
              <Button onClick={handleCSVPreview} disabled={csvImporting} className="w-full bg-zinc-800 text-zinc-200 hover:bg-zinc-700" data-testid="csv-preview-btn">
                {csvImporting ? 'Parsing...' : (language === 'IT' ? 'Anteprima' : 'Preview')}
              </Button>
            )}

            {/* Preview table */}
            {csvPreview && (
              <div className="space-y-3" data-testid="csv-preview-section">
                {/* Detection info banner */}
                {csvPreview.detection && (
                  <div className="flex items-center gap-3 p-2.5 rounded-sm bg-zinc-800/60 border border-zinc-700" data-testid="csv-detection-banner">
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${csvPreview.detection.confidence >= 0.7 ? 'bg-emerald-400' : csvPreview.detection.confidence >= 0.4 ? 'bg-amber-400' : 'bg-zinc-500'}`} />
                      <span className="text-xs font-semibold text-zinc-200" data-testid="detected-format-name">
                        {csvPreview.detection.format_name}
                      </span>
                    </div>
                    <span className="text-[10px] text-zinc-500">
                      sep: <span className="text-zinc-400 font-mono">{csvPreview.detection.separator === ';' ? 'semicolon' : 'comma'}</span>
                    </span>
                    <span className="text-[10px] text-zinc-500">
                      {csvPreview.detection.mapped_columns}/{csvPreview.detection.total_csv_columns} columns mapped
                    </span>
                    {csvPreview.detection.unmapped_columns > 0 && (
                      <span className="text-[10px] text-zinc-600">{csvPreview.detection.unmapped_columns} ignored</span>
                    )}
                  </div>
                )}

                {/* Warnings for missing core/optional fields */}
                {csvPreview.warnings?.length > 0 && (
                  <div className="space-y-1" data-testid="csv-warnings">
                    {csvPreview.warnings.map((w, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs px-2.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-sm">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span className="text-amber-300">{w}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Mapped fields info */}
                {csvPreview.detection?.core_fields_present?.length > 0 && (
                  <div className="text-[10px] text-zinc-500 px-1" data-testid="csv-field-summary">
                    <span className="text-emerald-400/70">Core: </span>
                    {csvPreview.detection.core_fields_present.map((f, i) => (
                      <span key={f} className="text-zinc-400">{i > 0 ? ', ' : ''}{f.replace('_', ' ')}</span>
                    ))}
                    {csvPreview.detection.optional_fields_present?.length > 0 && (
                      <>
                        <span className="text-blue-400/70 ml-2">Optional: </span>
                        {csvPreview.detection.optional_fields_present.map((f, i) => (
                          <span key={f} className="text-zinc-400">{i > 0 ? ', ' : ''}{f.replace('_', ' ')}</span>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {/* Match summary */}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-400">{csvPreview.matched} matched</span>
                  {csvPreview.suggested > 0 && <span className="text-blue-400">{csvPreview.suggested} suggested</span>}
                  {csvPreview.unmatched?.length > 0 && <span className="text-amber-400">{csvPreview.unmatched.length} unmatched</span>}
                  {Object.keys(matchOverrides).length > 0 && <span className="text-cyan-400">+{Object.keys(matchOverrides).length} manual</span>}
                  <span className="text-zinc-500">{csvPreview.total_rows} total</span>
                </div>
                <div className="overflow-x-auto max-h-72">
                  <table className="w-full text-xs" data-testid="csv-preview-table">
                    <thead>
                      <tr className="border-b border-zinc-800">
                        <th className="text-left p-1.5 text-zinc-500 font-semibold">Player</th>
                        <th className="text-left p-1.5 text-zinc-500 font-semibold">Match</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">Min</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">Dist</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">D/min</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">SHI</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">S7</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">AccHI</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">DecHI</th>
                        <th className="text-right p-1.5 text-zinc-500 font-semibold">SMax</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.rows.map((row, idx) => {
                        const hasOverride = matchOverrides[row.csv_player];
                        const overrideAthlete = hasOverride ? csvPreview.available_athletes?.find(a => a.id === hasOverride) : null;
                        const isMatched = row.match_status === 'exact' || row.match_status === 'high';
                        const isSuggested = row.match_status === 'suggested';
                        const willImport = isMatched || hasOverride;
                        return (
                        <tr key={idx} className={`border-b border-zinc-800/50 ${!willImport ? 'opacity-60' : ''}`} data-testid={`csv-preview-row-${idx}`}>
                          <td className="p-1.5">
                            <div className="text-zinc-100">{row.csv_player}</div>
                            {isMatched && <div className="text-[10px] text-emerald-400">{row.athlete_name}</div>}
                            {isSuggested && !hasOverride && row.athlete_name && <div className="text-[10px] text-blue-400">? {row.athlete_name}</div>}
                            {hasOverride && overrideAthlete && <div className="text-[10px] text-cyan-400">{overrideAthlete.name}</div>}
                          </td>
                          <td className="p-1.5">
                            {isMatched ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-emerald-500/15 text-emerald-400 rounded" data-testid={`match-status-${idx}`}>{row.match_status}</span>
                            ) : hasOverride ? (
                              <span className="text-[10px] px-1.5 py-0.5 bg-cyan-500/15 text-cyan-400 rounded" data-testid={`match-status-${idx}`}>manual</span>
                            ) : isSuggested ? (
                              <div className="flex items-center gap-1">
                                <select
                                  className="bg-zinc-900 border border-blue-500/30 text-blue-300 text-[10px] rounded px-1 py-0.5 max-w-[120px]"
                                  data-testid={`match-select-${idx}`}
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) setMatchOverrides(prev => ({...prev, [row.csv_player]: e.target.value}));
                                  }}
                                >
                                  <option value="">Confirm...</option>
                                  {row.suggested_athlete_id && <option value={row.suggested_athlete_id}>{row.athlete_name}</option>}
                                  {csvPreview.available_athletes?.filter(a => a.id !== row.suggested_athlete_id).map(a => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                  ))}
                                </select>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <select
                                  className="bg-zinc-900 border border-amber-500/30 text-amber-300 text-[10px] rounded px-1 py-0.5 max-w-[120px]"
                                  data-testid={`match-select-${idx}`}
                                  value=""
                                  onChange={(e) => {
                                    if (e.target.value) setMatchOverrides(prev => ({...prev, [row.csv_player]: e.target.value}));
                                  }}
                                >
                                  <option value="">Assign...</option>
                                  {csvPreview.available_athletes?.map(a => (
                                    <option key={a.id} value={a.id}>{a.name}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.session_minutes ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.distance_m?.toLocaleString() ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.distance_per_min ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.shi_distance?.toLocaleString() ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.s7_distance ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.acchi_distance ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.dechi_distance ?? '-'}</td>
                          <td className="p-1.5 text-right text-zinc-300 font-mono">{row.metrics?.speed_max ?? '-'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => { setShowImportCSV(false); setCsvPreview(null); }} className="border-zinc-700">
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            {csvPreview ? (
              <Button onClick={handleCSVImport} disabled={csvImporting || (csvPreview.matched + Object.keys(matchOverrides).length) === 0} className="bg-blue-500 text-white hover:bg-blue-400" data-testid="import-submit">
                {csvImporting ? 'Importing...' : (language === 'IT' ? `Importa ${csvPreview.matched + Object.keys(matchOverrides).length} atleti` : `Import ${csvPreview.matched + Object.keys(matchOverrides).length} athletes`)}
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Settings className="w-5 h-5 text-zinc-400" />
              {language === 'IT' ? 'Impostazioni GPS per Squadra' : 'Team GPS Settings'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-zinc-500">
              {language === 'IT' 
                ? 'Configura la modalità di tracciamento carichi per ogni squadra.'
                : 'Configure load tracking mode for each team.'}
            </p>
            <div className="space-y-3">
              {teamSettings.map(setting => (
                <div key={setting.team_id} className="flex items-center justify-between p-3 bg-zinc-950/50 border border-zinc-800 rounded-sm">
                  <div>
                    <span className="text-amber-500 font-semibold">{setting.team_code}</span>
                    <span className="text-zinc-400 ml-2">{setting.team_name}</span>
                  </div>
                  <Select 
                    value={setting.load_mode} 
                    onValueChange={(v) => handleUpdateTeamMode(setting.team_id, v)}
                  >
                    <SelectTrigger className="w-32 bg-zinc-900 border-zinc-700" data-testid={`setting-${setting.team_code}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      <SelectItem value="GPS">
                        <span className="flex items-center gap-2">
                          <MapPin className="w-3 h-3 text-blue-400" /> GPS
                        </span>
                      </SelectItem>
                      <SelectItem value="NO_GPS">
                        <span className="flex items-center gap-2">
                          <Activity className="w-3 h-3 text-zinc-400" /> No GPS
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)} className="bg-zinc-800 text-zinc-100 hover:bg-zinc-700">
              {language === 'IT' ? 'Chiudi' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* RTP Exclusion Modal */}
      <Dialog open={showRtpModal} onOpenChange={() => setShowRtpModal(false)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-500" />
              {language === 'IT' ? 'Atleti in RTP Trovati' : 'RTP Athletes Found'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm text-zinc-400">
              {rtpAthletes.length} {language === 'IT' ? 'atleti hanno profili RTP attivi' : 'athletes have active RTP profiles'}:
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {rtpAthletes.map(a => (
                <div key={a.athlete_id} className="flex items-center justify-between p-2 bg-zinc-950/50 border border-zinc-800 rounded-sm" data-testid={`rtp-athlete-${a.athlete_id}`}>
                  <span className="text-zinc-200 text-sm">{a.athlete_name}</span>
                  <span className="text-xs text-amber-400">{a.injury_type} - {a.current_phase?.replace('_', ' ')}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-zinc-500">
              {language === 'IT' ? 'Vuoi includerli?' : 'Include them in this event?'}
            </p>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => handleRtpDecision('cancel')} className="border-zinc-700" data-testid="rtp-cancel-btn">
              {language === 'IT' ? 'Annulla' : 'Cancel'}
            </Button>
            <Button onClick={() => handleRtpDecision('exclude')} className="bg-rose-600 text-white hover:bg-rose-500" data-testid="rtp-exclude-btn">
              {language === 'IT' ? 'Escludi' : 'Exclude'}
            </Button>
            <Button onClick={() => handleRtpDecision('include')} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="rtp-include-btn">
              {language === 'IT' ? 'Includi' : 'Include'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Event Detail Dialog */}
      {showEventDetail && (
        <EventDetailDialog 
          event={showEventDetail}
          onClose={() => setShowEventDetail(null)}
          onUpdate={() => fetchData({ background: showEventDetail.event_type === 'TRAINING' })}
          api={api}
          language={language}
          canEdit={canEdit}
        />
      )}
    </div>
  );
};

// Event Detail Dialog Component
const AnalyticsPanel = ({ selectedTeam, teams, api, language, range, setRange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const t = (en, it) => (language === 'IT' ? it : en);
  const team = teams.find((x) => x.id === selectedTeam);

  const dateFromForRange = (r) => {
    if (r === 'all') return null;
    const days = r === '7d' ? 7 : 30;
    // Inclusive of today → today - (days - 1) calendar days
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (days - 1));
    // Local YYYY-MM-DD (avoid UTC off-by-one)
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    // Explicit "all teams" state — do not fetch, do not confuse with "no team selected"
    if (!selectedTeam || selectedTeam === 'all') {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const df = dateFromForRange(range);
    const url = df
      ? `/gps/analytics/team/${selectedTeam}?date_from=${df}`
      : `/gps/analytics/team/${selectedTeam}`;
    api
      .get(url)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.detail || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [api, selectedTeam, range]);

  if (!selectedTeam || selectedTeam === 'all') {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800" data-testid="analytics-all-teams-state">
        <CardHeader>
          <CardTitle className="text-zinc-100">{t('Load Analytics', 'Analisi Carichi')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-zinc-500">
            {t(
              'Select a specific team to view detailed analytics.',
              "Seleziona una squadra specifica per visualizzare l'analisi dettagliata."
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Segmented control (reused across loading / empty / data states)
  const RangePicker = (
    <div className="flex items-center gap-1 rounded-md border border-zinc-800 bg-zinc-900/50 p-0.5" data-testid="analytics-range-picker">
      {[
        { id: '7d', label: t('7d', '7 gg') },
        { id: '30d', label: t('30d', '30 gg') },
        { id: 'all', label: t('All', 'Tutto') },
      ].map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => setRange(opt.id)}
          className={`px-2.5 py-1 text-xs rounded transition-colors ${
            range === opt.id
              ? 'bg-amber-500 text-zinc-950 font-semibold'
              : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/60'
          }`}
          data-testid={`analytics-range-${opt.id}`}
          aria-pressed={range === opt.id}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  const rangeLabel = range === '7d'
    ? t('last 7 days', 'ultimi 7 giorni')
    : range === '30d'
      ? t('last 30 days', 'ultimi 30 giorni')
      : t('all time', 'tutto il periodo');

  const rangeEmptyMessage = range === '7d'
    ? t('No load data in the last 7 days.', 'Nessun dato di carico negli ultimi 7 giorni.')
    : range === '30d'
      ? t('No load data in the last 30 days.', 'Nessun dato di carico negli ultimi 30 giorni.')
      : t('No load data available.', 'Nessun dato di carico disponibile.');

  const TeamHeader = (
    <div className="flex items-center justify-between flex-wrap gap-2" data-testid="analytics-header">
      <div>
        <h3 className="text-lg font-semibold text-zinc-100">
          {t('Load Analytics', 'Analisi Carichi')} — {team ? `${team.code} · ${team.name}` : selectedTeam}
        </h3>
        <p className="text-xs text-zinc-500">
          {t('Aggregated from canonical GPS/Load rows', 'Aggregato dalle righe canoniche GPS/Carico')} · <span className="text-zinc-400">{rangeLabel}</span>
        </p>
      </div>
      {RangePicker}
    </div>
  );

  if (loading && !data) {
    return (
      <div className="space-y-4">
        {TeamHeader}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 text-center text-zinc-500" data-testid="analytics-loading">
            <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
            {t('Loading analytics…', 'Caricamento analisi…')}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {TeamHeader}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-6 text-rose-400" data-testid="analytics-error">
            {String(error)}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data || data.total_rows === 0) {
    return (
      <div className="space-y-4">
        {TeamHeader}
        <Card className="bg-zinc-900/50 border-zinc-800" data-testid="analytics-empty">
          <CardContent className="p-6">
            <p className="text-zinc-500">{rangeEmptyMessage}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const athleteAvg = data.athlete_averages || [];
  const orgAvgLoad = athleteAvg.length
    ? Math.round(athleteAvg.reduce((s, a) => s + (a.avg_load || 0), 0) / athleteAvg.length)
    : 0;
  const rpeVals = athleteAvg.filter((a) => a.avg_rpe != null).map((a) => a.avg_rpe);
  const orgAvgRpe = rpeVals.length
    ? Math.round((rpeVals.reduce((s, v) => s + v, 0) / rpeVals.length) * 10) / 10
    : null;

  return (
    <div className="space-y-4" data-testid="analytics-content">
      {TeamHeader}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">{t('Events', 'Eventi')}</div>
            <div className="text-2xl font-bold text-zinc-100" data-testid="analytics-kpi-events">{data.total_events}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">{t('Athlete rows', 'Righe atleta')}</div>
            <div className="text-2xl font-bold text-zinc-100" data-testid="analytics-kpi-rows">{data.total_rows}</div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">{t('Avg load', 'Carico medio')}</div>
            <div className="text-2xl font-bold text-emerald-400" data-testid="analytics-kpi-avg-load">
              {orgAvgLoad ? orgAvgLoad.toLocaleString() : '-'}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="text-xs text-zinc-500">{t('Avg RPE', 'RPE medio')}</div>
            <div className="text-2xl font-bold text-amber-400" data-testid="analytics-kpi-avg-rpe">
              {orgAvgRpe != null ? orgAvgRpe : '-'}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 text-base">
            {t('Athlete leaderboard (by total load)', 'Classifica atleti (per carico totale)')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">#</th>
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">{t('Athlete', 'Atleta')}</th>
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">Pos</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Sessions', 'Sessioni')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Avg RPE', 'RPE medio')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Avg load', 'Carico medio')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Avg dist', 'Dist media')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Total load', 'Carico totale')}</th>
                </tr>
              </thead>
              <tbody>
                {athleteAvg.map((a, i) => (
                  <tr key={a.athlete_id} className="border-b border-zinc-800/50" data-testid={`analytics-athlete-row-${a.athlete_id}`}>
                    <td className="p-2 text-zinc-500">{i + 1}</td>
                    <td className="p-2 text-zinc-100">{a.athlete_name}</td>
                    <td className="p-2 text-zinc-500">{a.position || '-'}</td>
                    <td className="p-2 text-right text-zinc-300">{a.sessions}</td>
                    <td className="p-2 text-right text-amber-400 font-mono">{a.avg_rpe ?? '-'}</td>
                    <td className="p-2 text-right text-emerald-400 font-mono">{a.avg_load ? a.avg_load.toLocaleString() : '-'}</td>
                    <td className="p-2 text-right text-zinc-300 font-mono">{a.avg_distance ? a.avg_distance.toLocaleString() : '-'}</td>
                    <td className="p-2 text-right text-emerald-300 font-mono">{a.total_load ? a.total_load.toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-zinc-100 text-base">
            {t('Last events', 'Ultimi eventi')} <span className="text-xs text-zinc-500 font-normal">({data.event_summaries?.length || 0})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">{t('Date', 'Data')}</th>
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">{t('Type', 'Tipo')}</th>
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">{t('Title', 'Titolo')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Athletes', 'Atleti')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Avg RPE', 'RPE medio')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('Total load', 'Carico totale')}</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">{t('RPE %', 'RPE %')}</th>
                </tr>
              </thead>
              <tbody>
                {(data.event_summaries || []).map((e) => (
                  <tr key={e.event_id} className="border-b border-zinc-800/50" data-testid={`analytics-event-row-${e.event_id}`}>
                    <td className="p-2 text-zinc-300 whitespace-nowrap">{e.date}</td>
                    <td className="p-2 text-zinc-400">{e.event_type}</td>
                    <td className="p-2 text-zinc-100">{e.title || '-'}</td>
                    <td className="p-2 text-right text-zinc-300">{e.athlete_count}</td>
                    <td className="p-2 text-right text-amber-400 font-mono">{e.avg_rpe ?? '-'}</td>
                    <td className="p-2 text-right text-emerald-400 font-mono">{e.total_load ? e.total_load.toLocaleString() : '-'}</td>
                    <td className="p-2 text-right text-zinc-300 font-mono">{e.rpe_compliance}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};



const EventDetailDialog = ({ event, onClose, onUpdate, api, language, canEdit }) => {
  const isTraining = event.event_type === 'TRAINING';
  const [trainingContext, setTrainingContext] = useState(null);
  const [trainingErrorMessage, setTrainingErrorMessage] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averages, setAverages] = useState(null);
  const [minMinutes, setMinMinutes] = useState(null);
  // Add-athlete state
  const [teamAthletes, setTeamAthletes] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState([]);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  // Draft-edit state (P0 iter114): all field edits stay local until "Save changes" is pressed
  // Shape: { [rowId]: { match_minutes?, post_match_work?, post_match_minutes?, rpe? } }
  const [drafts, setDrafts] = useState({});
  const [savingDrafts, setSavingDrafts] = useState(false);

  const refreshRows = React.useCallback(async () => {
    let res;
    if (isTraining) {
      try {
        const [eventRes, contextRes, avgRes] = await Promise.all([
          api.get(`/gps/events/${event.id}`), api.get(`/gps/events/${event.id}/training-duration`),
          api.get(`/gps/team/${event.team_id}/averages/${event.id}`),
        ]);
        res = eventRes;
        setTrainingContext(contextRes.data);
        setAverages(avgRes.data);
        setTrainingErrorMessage('');
      } catch (err) {
        setTrainingContext(null);
        setTrainingErrorMessage(trainingError(err, language));
        throw err;
      }
    } else {
      res = await api.get(`/gps/events/${event.id}`);
    }
    setRows(res.data.athlete_rows);
    // Drop draft entries for rows that no longer exist server-side (e.g. removed athlete)
    setDrafts((prev) => {
      const validIds = new Set(res.data.athlete_rows.map((r) => r.id));
      const next = {};
      for (const [rowId, patch] of Object.entries(prev)) {
        if (validIds.has(rowId)) next[rowId] = patch;
      }
      return next;
    });
    return res.data.athlete_rows;
  }, [api, event.id, event.team_id, isTraining, language]);

  useEffect(() => {
    const fetchRows = async () => {
      try {
        await refreshRows();
        if (isTraining) return;
        let avgUrl = `/gps/team/${event.team_id}/averages/${event.id}`;
        if (minMinutes) {
          avgUrl += `?min_minutes=${minMinutes}`;
        }
        const avgRes = await api.get(avgUrl);
        setAverages(avgRes.data);
      } catch (err) {
        console.error('Failed to fetch event details:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchRows();
  }, [api, event.id, event.team_id, minMinutes, refreshRows, isTraining]);

  // Load team athletes when the picker opens
  useEffect(() => {
    if (!pickerOpen || teamAthletes.length) return;
    api.get(`/athletes?team_id=${event.team_id}`).then(r => setTeamAthletes(r.data || []));
  }, [pickerOpen, api, event.team_id, teamAthletes.length]);

  const assignedIds = new Set(rows.map(r => r.athlete_id));
  const availableAthletes = teamAthletes.filter(a => !assignedIds.has(a.id) && a.is_active !== false);
  const filteredAthletes = pickerSearch
    ? availableAthletes.filter(a => {
        const q = pickerSearch.toLowerCase();
        return (`${a.first_name || ''} ${a.last_name || ''}`.toLowerCase().includes(q))
          || (a.position || '').toLowerCase().includes(q);
      })
    : availableAthletes;

  const handleAddAthletes = async () => {
    if (pickerSelected.length === 0) return;
    setAdding(true);
    try {
      const res = await api.post(`/gps/events/${event.id}/athletes/batch`, {
        athlete_ids: pickerSelected,
      });
      const added = res.data.added?.length || 0;
      const skipped = res.data.skipped?.length || 0;
      if (added > 0) {
        toast.success(language === 'IT'
          ? `Aggiunti ${added} atleti${skipped ? ` (${skipped} saltati)` : ''}`
          : `Added ${added} athlete${added !== 1 ? 's' : ''}${skipped ? ` (${skipped} skipped)` : ''}`);
      } else {
        toast.error(language === 'IT' ? 'Nessun atleta aggiunto' : 'No athletes added');
      }
      await refreshRows();
      onUpdate();
      setPickerOpen(false);
      setPickerSelected([]);
      setPickerSearch('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add athletes');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAthlete = async (row) => {
    // First attempt without force → 200 (no RPE) OR 409 (has RPE)
    const hasSubmission = row.rpe != null || row.training_load != null
      || (row.rpe_status && row.rpe_status !== 'MISSING');
    const softMsg = language === 'IT'
      ? `Rimuovere ${row.athlete_name} da questa seduta?`
      : `Remove ${row.athlete_name} from this event?`;
    const hardMsg = language === 'IT'
      ? `Questo atleta ha già un RPE registrato. Rimuovendolo dalla seduta verranno rimossi anche i dati associati a questo evento. Continuare?`
      : `This athlete already has a submitted RPE. Removing them will also delete the RPE/Load data associated with this event. Continue?`;
    if (!window.confirm(hasSubmission ? hardMsg : softMsg)) return;
    setRemovingId(row.id);
    try {
      const url = hasSubmission
        ? `/gps/events/${event.id}/athletes/${row.athlete_id}?force=true`
        : `/gps/events/${event.id}/athletes/${row.athlete_id}`;
      await api.delete(url);
      toast.success(language === 'IT' ? 'Atleta rimosso' : 'Athlete removed');
      await refreshRows();
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove athlete');
    } finally {
      setRemovingId(null);
    }
  };

  const handleUpdateRPE = (rowId, rawValue) => {
    // Local-only draft update. Empty string clears the field to null (mancante).
    setDrafts((prev) => {
      const cur = prev[rowId] || {};
      const parsed = rawValue === '' || rawValue == null ? null : isTraining ? Number(rawValue) : parseInt(rawValue);
      // Ignore obviously invalid values so we don't build a bad draft
      if (!isTraining && parsed != null && (Number.isNaN(parsed) || parsed < 0 || parsed > 10)) return prev;
      return { ...prev, [rowId]: { ...cur, rpe: parsed } };
    });
  };

  const handleUpdateTrainingMinutes = (rowId, rawValue) => {
    const value = rawValue === null ? null : rawValue === '' ? '' : Number(rawValue);
    setDrafts(prev => ({ ...prev, [rowId]: { ...prev[rowId], training_duration_override_min: value } }));
  };

  const handleUpdateMatchMinutes = (rowId, field, value) => {
    // Local-only draft update for match_minutes / post_match_work / post_match_minutes.
    //
    // P0.5.16 — Canonical semantics enforced:
    //   • empty string   → null  ("not entered" / MISSING)
    //   • "0"            → 0     (valid DNP / known zero)
    //   • "45"           → 45    (actual minutes)
    // Do NOT use truthiness (`|| 0`) — it collapses the null↔0 distinction.
    setDrafts((prev) => {
      const cur = prev[rowId] || {};
      const patch = { ...cur };
      if (field === 'match_minutes') {
        const v = (value === '' || value == null) ? null : parseInt(value, 10);
        if (v != null && Number.isNaN(v)) return prev;
        patch.match_minutes = v;
      } else if (field === 'post_match_work') {
        patch.post_match_work = !!value;
        if (!value) patch.post_match_minutes = 0;
      } else if (field === 'post_match_minutes') {
        const v = (value === '' || value == null) ? null : parseInt(value, 10);
        if (v != null && Number.isNaN(v)) return prev;
        patch.post_match_minutes = v;
      }
      return { ...prev, [rowId]: patch };
    });
  };

  // --- Draft/dirty helpers -------------------------------------------------
  const getFieldValue = (row, field) => {
    const draft = drafts[row.id];
    if (draft && Object.prototype.hasOwnProperty.call(draft, field)) return draft[field];
    return row[field];
  };
  const isRowDirty = (row) => {
    const d = drafts[row.id];
    if (!d) return false;
    for (const [k, v] of Object.entries(d)) {
      const serverVal = row[k];
      // Normalise numeric/boolean/null equality
      if ((serverVal ?? null) !== (v ?? null)) return true;
    }
    return false;
  };
  const dirtyRows = rows.filter(isRowDirty);
  const isDirty = dirtyRows.length > 0;
  const invalidTrainingDraft = isTraining && dirtyRows.some(row => {
    const d = drafts[row.id];
    const minutes = d.training_duration_override_min;
    return (Object.prototype.hasOwnProperty.call(d, 'training_duration_override_min') && minutes !== null &&
      (minutes === '' || !Number.isSafeInteger(minutes) || minutes < 0)) ||
      (Object.prototype.hasOwnProperty.call(d, 'rpe') && (d.rpe == null ? row.rpe != null :
        !Number.isInteger(d.rpe) || d.rpe < 0 || d.rpe > 10));
  });

  const handleDiscardChanges = () => {
    if (!isDirty) return;
    const msg = language === 'IT'
      ? 'Scartare tutte le modifiche non salvate?'
      : 'Discard all unsaved changes?';
    if (!window.confirm(msg)) return;
    setDrafts({});
  };

  const handleSaveChanges = async () => {
    if (!isDirty || savingDrafts || invalidTrainingDraft || (isTraining && !trainingContext)) return;
    setSavingDrafts(true);
    try {
      // Per-row SEQUENTIAL writes (match-minutes then rpe) to avoid a race where
      // both handlers fetch the row before either persists — training_load is
      // recomputed from `row.rpe` × total_minutes, so the second call must see
      // the first call's committed state. Different rows run in PARALLEL.
      const perRow = dirtyRows.map((row) => async () => {
        const d = drafts[row.id];
        if (!d) return;
        if (isTraining) {
          const payload = {};
          if (Object.prototype.hasOwnProperty.call(d, 'training_duration_override_min')) payload.override_minutes = d.training_duration_override_min;
          if (Object.prototype.hasOwnProperty.call(d, 'rpe') && d.rpe != null) payload.rpe = d.rpe;
          if (Object.keys(payload).length) await api.put(`/gps/events/${event.id}/athletes/${row.athlete_id}/training-duration`, payload);
          return;
        }
        const touchesMatch = ['match_minutes', 'post_match_work', 'post_match_minutes']
          .some((k) => Object.prototype.hasOwnProperty.call(d, k));
        const touchesRpe = Object.prototype.hasOwnProperty.call(d, 'rpe');
        if (touchesMatch) {
          // P0.5.16 — Canonical semantics: use `??` (nullish) NOT `||`,
          // so that `0` (valid DNP) is preserved and only `null`/`undefined`
          // fall back to the server row value. Empty draft → null (MISSING).
          const mm  = d.match_minutes         !== undefined ? d.match_minutes         : row.match_minutes;
          const pmw = d.post_match_work       !== undefined ? d.post_match_work       : (row.post_match_work || false);
          const pmm = d.post_match_minutes    !== undefined ? d.post_match_minutes    : row.post_match_minutes;
          const payload = {
            match_minutes: mm,          // may be null (missing) or 0 (DNP)
            post_match_work: pmw,
            post_match_minutes: pmw ? (pmm ?? 0) : 0,
          };
          await api.put(`/gps/rows/${row.id}/match-minutes`, payload);
        }
        if (touchesRpe && d.rpe != null) {
          await api.put(`/gps/rows/${row.id}/rpe`, { rpe: d.rpe });
        }
      });
      const results = await Promise.allSettled(perRow.map((fn) => fn()));
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        const firstErr = failed[0].reason?.response?.data?.detail || 'Save failed for some rows';
        toast.error(isTraining ? trainingError(failed[0].reason, language) : String(firstErr));
      }
      // ONE canonical refetch after all mutations
      const fresh = await refreshRows();
      // Drop drafts that now match server state
      setDrafts((prev) => {
        const next = {};
        const byId = Object.fromEntries(fresh.map((r) => [r.id, r]));
        for (const [rowId, patch] of Object.entries(prev)) {
          const srv = byId[rowId];
          if (!srv) continue;
          let stillDirty = false;
          for (const [k, v] of Object.entries(patch)) {
            if ((srv[k] ?? null) !== (v ?? null)) { stillDirty = true; break; }
          }
          if (stillDirty) next[rowId] = patch;
        }
        return next;
      });
      if (failed.length === 0) {
        toast.success(language === 'IT' ? 'Modifiche salvate' : 'Changes saved');
      }
      onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingDrafts(false);
    }
  };

  // p056 — Delete entire event (reuses iter_123-hardened DELETE /gps/events/{id})
  const [showDeleteEvent, setShowDeleteEvent] = useState(false);
  const [deletingEvent, setDeletingEvent] = useState(false);
  const hasSubmissions = (event.rpe_submitted_count || 0) > 0 || rows.some(
    (r) => r.rpe != null || r.training_load != null || (r.rpe_status && r.rpe_status !== 'MISSING')
  );

  const handleDeleteEvent = async () => {
    setDeletingEvent(true);
    try {
      await api.delete(`/gps/events/${event.id}`);
      toast.success(language === 'IT' ? 'Evento eliminato' : 'Event deleted');
      setShowDeleteEvent(false);
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeletingEvent(false);
    }
  };

  // Close guard — confirm if there are unsaved drafts
  const requestClose = () => {
    if (isDirty) {
      const msg = language === 'IT'
        ? 'Hai modifiche non salvate. Uscire senza salvarle?'
        : 'You have unsaved changes. Exit without saving?';
      if (!window.confirm(msg)) return;
    }
    onClose();
  };

  const isMatch = event.event_type === 'MATCH' || event.event_type === 'FRIENDLY_MATCH';
  const hasGPSMetrics = event.source === 'GPS_IMPORT' || event.source === 'CSV_IMPORT' || rows.some(r => r.metrics?.distance_m);

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) requestClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-zinc-900 border-zinc-800 grid-cols-1 sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="event-detail-dialog" onEscapeKeyDown={(e) => { e.preventDefault(); requestClose(); }} onPointerDownOutside={(e) => { if (isDirty) e.preventDefault(); }}>
        <DialogHeader>
          <DialogTitle className="text-zinc-100">
            <div className={isTraining ? 'flex flex-col items-stretch gap-3 pr-6 sm:flex-row sm:items-start sm:justify-between' : 'flex flex-wrap items-center justify-between gap-3'}>
              <div className={isTraining ? 'min-w-0 flex-1 space-y-1 text-left' : 'flex items-center gap-3 min-w-0 flex-1'}>
                {isTraining ? <>
                  <span className="block text-sm text-amber-500 font-semibold" data-testid="training-header-team">{event.team_code || event.team_name} · TRAINING</span>
                  <span className="block whitespace-normal break-words" data-testid="training-header-title">{event.title}</span>
                  <span className="block text-zinc-500 text-sm font-normal" data-testid="training-header-date">{event.date}</span>
                </> : <>
                  <span className="text-amber-500 font-bold">{event.team_code}</span>
                  <span className="truncate">{event.title}</span>
                  <span className="text-zinc-500 text-sm font-normal shrink-0">{event.date}</span>
                </>}
              </div>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className={`border-rose-500/40 text-rose-400 hover:bg-rose-500/10 h-11 sm:h-9 min-h-[44px] sm:min-h-0 shrink-0 ${isTraining ? 'self-start' : ''}`}
                  onClick={() => setShowDeleteEvent(true)}
                  data-testid="delete-event-btn"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  <span className="text-xs font-semibold">
                    {language === 'IT' ? 'Elimina evento' : 'Delete event'}
                  </span>
                </Button>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {isTraining && <TrainingDurationPanel api={api} event={event} context={trainingContext} error={trainingErrorMessage}
            language={language} disabled={savingDrafts || isDirty} canEdit={canEdit}
            onUpdate={async () => { await refreshRows(); onUpdate(); }} />}
          {/* Basketball Load Model banner — visible when event has a basketball load_model. */}
          {(event.load_model === 'basketball_active_passive' || event.load_model === 'basketball_legacy_duration') && (
            <div
              className={`rounded-md border p-3 text-xs ${
                event.load_model === 'basketball_legacy_duration'
                  ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
                  : 'border-amber-500/30 bg-amber-500/5 text-amber-300'
              }`}
              data-testid="basketball-load-banner"
            >
              {event.load_model === 'basketball_legacy_duration' ? (
                <>
                  <strong>{language === 'IT' ? 'Carico legacy basket' : 'Legacy basketball load'}.</strong>{' '}
                  {language === 'IT'
                    ? 'Questo evento usa il carico basato sulla durata. Aggiungi i minuti attivi per abilitare il calcolo attivo/passivo.'
                    : 'This basketball load event uses legacy duration-based load. Add active minutes to enable active/passive load calculation.'}
                </>
              ) : (
                <span className="flex flex-wrap gap-x-4 gap-y-1">
                  <span>🏀 <strong>{language === 'IT' ? 'Modello Attivo/Passivo' : 'Active/Passive Model'}</strong></span>
                  <span>{language === 'IT' ? 'Attivi' : 'Active'}: <strong data-testid="event-active-minutes">{event.active_minutes ?? '—'}m</strong></span>
                  <span>{language === 'IT' ? 'Passivi' : 'Passive'}: <strong data-testid="event-passive-minutes">{event.passive_minutes ?? '—'}m</strong></span>
                  <span>{language === 'IT' ? 'Fattore' : 'Factor'}: <strong data-testid="event-passive-factor">{event.passive_load_factor ?? '—'}</strong></span>
                  <span>{language === 'IT' ? 'Min Aggiustati' : 'Adjusted Min'}: <strong data-testid="event-adjusted-minutes">{event.adjusted_load_minutes ?? '—'}</strong></span>
                </span>
              )}
            </div>
          )}

          {/* Event Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-zinc-500">{language === 'IT' ? 'Tipo:' : 'Type:'}</span>
              <span className="ml-2 text-zinc-100">{event.event_type.replace('_', ' ')}</span>
            </div>
            <div>
              <span className="text-zinc-500">{language === 'IT' ? 'Fonte:' : 'Source:'}</span>
              <span className="ml-2">{event.source === 'GPS_IMPORT' ? <span className="text-blue-400">GPS Import</span> : <span className="text-zinc-400">Manual</span>}</span>
            </div>
            <div>
              <span className="text-zinc-500">{language === 'IT' ? 'Atleti:' : 'Athletes:'}</span>
              <span className="ml-2 text-zinc-100" data-testid="event-athlete-count">{isTraining ? rows.length : event.athlete_count}</span>
            </div>
            <div>
              <span className="text-zinc-500">RPE:</span>
              <span className="ml-2 text-emerald-400" data-testid="event-rpe-received">{isTraining ? rows.filter(r => r.rpe != null).length : event.rpe_submitted_count}</span>
              <span className="text-zinc-600">/</span>
              <span className="text-zinc-400" data-testid="event-rpe-total">{isTraining ? rows.length : event.athlete_count}</span>
            </div>
          </div>

          {/* Team Averages */}
          {averages && averages.athlete_count > 0 && (
            <Card className="bg-zinc-950/50 border-zinc-800">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-zinc-100 text-sm">
                    {language === 'IT' ? 'Medie Squadra' : 'Team Averages'}
                    {minMinutes && <span className="text-zinc-500 font-normal ml-2">({minMinutes}+ min)</span>}
                  </CardTitle>
                  {isMatch && (
                    <Select value={minMinutes?.toString() || 'all'} onValueChange={(v) => setMinMinutes(v === 'all' ? null : parseInt(v))}>
                      <SelectTrigger className="w-36 h-8 bg-zinc-900 border-zinc-700 text-xs">
                        <SelectValue placeholder="All players" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="all">All players</SelectItem>
                        <SelectItem value="60">60+ min only</SelectItem>
                        <SelectItem value="45">45+ min only</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 text-center">
                  {hasGPSMetrics && (
                    <>
                      <div>
                        <div className="text-lg font-bold text-zinc-100">{averages.averages.distance_m?.toLocaleString() || '-'}</div>
                        <div className="text-xs text-zinc-500">Distance (m)</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-zinc-100">{averages.averages.shi_distance?.toLocaleString() || '-'}</div>
                        <div className="text-xs text-zinc-500">SHI Dist</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-zinc-100">{averages.averages.s7_distance?.toLocaleString() || '-'}</div>
                        <div className="text-xs text-zinc-500">Sprint Dist</div>
                      </div>
                    </>
                  )}
                  <div>
                    <div className="text-lg font-bold text-amber-400" data-testid="event-average-rpe">{isTraining ? (averages.averages.rpe ?? '—') : (averages.averages.rpe || '-')}</div>
                    <div className="text-xs text-zinc-500">Avg RPE</div>
                  </div>
                  <div>
                    <div className="text-lg font-bold text-emerald-400" data-testid="event-average-load">{isTraining ? (averages.averages.training_load?.toLocaleString() ?? '—') : (averages.averages.training_load?.toLocaleString() || '-')}</div>
                    <div className="text-xs text-zinc-500">Avg Load</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Athlete Rows */}
          {canEdit && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-xs text-zinc-500" data-testid="event-rpe-summary">
                  {language === 'IT'
                    ? `${rows.length} atleti • ${rows.filter(r=>r.rpe!=null).length} RPE inviati • ${rows.filter(r=>r.rpe==null).length} mancanti`
                    : `${rows.length} athletes • ${rows.filter(r=>r.rpe!=null).length} RPE submitted • ${rows.filter(r=>r.rpe==null).length} missing`}
                </div>
                {isDirty && (
                  <span
                    className="text-xs font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/40 rounded px-2 py-0.5"
                    data-testid="unsaved-indicator"
                  >
                    {language === 'IT'
                      ? `Modifiche non salvate (${dirtyRows.length})`
                      : `Unsaved changes (${dirtyRows.length})`}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {isDirty && (
                  <Button
                    size="sm" variant="outline"
                    onClick={handleDiscardChanges}
                    disabled={savingDrafts}
                    className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                    data-testid="discard-changes-btn"
                  >
                    {language === 'IT' ? 'Annulla modifiche' : 'Discard changes'}
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleSaveChanges}
                  disabled={!isDirty || savingDrafts || invalidTrainingDraft || (isTraining && !trainingContext)}
                  className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-zinc-500 text-white"
                  data-testid="save-changes-btn"
                >
                  {savingDrafts ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
                  {language === 'IT' ? 'Salva modifiche' : 'Save changes'}
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => { setPickerOpen(true); setPickerSelected([]); setPickerSearch(''); }}
                  className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                  data-testid="add-athlete-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  {language === 'IT' ? 'Aggiungi atleta' : 'Add athlete'}
                </Button>
              </div>
            </div>
          )}
          {invalidTrainingDraft && <p role="alert" className="text-xs text-red-400" data-testid="training-draft-error">
            {language === 'IT' ? 'Minuti interi ≥ 0 e RPE intero da 0 a 10. Un RPE già registrato non può essere cancellato da questo modulo.' : 'Use whole minutes ≥ 0 and a whole RPE from 0 to 10. A recorded RPE cannot be cleared from this form.'}
          </p>}
          <div className="overflow-x-auto">
            <table className={`w-full text-sm ${isTraining && !hasGPSMetrics ? 'training-athletes-table' : ''}`} data-testid="event-athlete-table">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left p-2 text-xs font-semibold text-zinc-500">{language === 'IT' ? 'Atleta' : 'Athlete'}</th>
                  {!isTraining && <th className="text-left p-2 text-xs font-semibold text-zinc-500">Pos</th>}
                  {hasGPSMetrics && (
                    <>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">Dist</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">D/min</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">SHI</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">Sprint</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">AccHI</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">DecHI</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">SMax</th>
                    </>
                  )}
                  {isMatch ? (
                    <>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">{language === 'IT' ? 'Min Gara' : 'Match Min'}</th>
                      <th className="text-center p-2 text-xs font-semibold text-zinc-500">{language === 'IT' ? 'Post' : 'Post'}</th>
                      <th className="text-right p-2 text-xs font-semibold text-zinc-500">{language === 'IT' ? 'Post Min' : 'Post Min'}</th>
                    </>
                  ) : (
                    <th className="text-right p-2 text-xs font-semibold text-zinc-500" data-testid="event-minutes-heading">
                      {isTraining ? (trainingContext?.planner_session_id ? (language === 'IT' ? 'Minuti TRAINING' : 'TRAINING minutes') : (language === 'IT' ? 'Minuti registrati' : 'Recorded minutes')) : 'Min'}
                    </th>
                  )}
                  <th className="text-center p-2 text-xs font-semibold text-zinc-500">RPE</th>
                  <th className="text-right p-2 text-xs font-semibold text-zinc-500">Load</th>
                  <th className="text-center p-2 text-xs font-semibold text-zinc-500">Status</th>
                  {canEdit && <th className="text-center p-2 text-xs font-semibold text-zinc-500 w-10"></th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={hasGPSMetrics ? (isMatch ? 14 : 13) : (isMatch ? 7 : 6)} className="p-4 text-center text-zinc-500">Loading...</td></tr>
                ) : rows.map(row => (
                  <tr key={row.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20" data-testid={`athlete-row-${row.id}`}>
                    <td className="p-2 text-zinc-100" data-cell="athlete" data-testid={`athlete-name-${row.id}`}>{row.athlete_name}</td>
                    {!isTraining && <td className="p-2 text-zinc-500">{row.athlete_position}</td>}
                    {hasGPSMetrics && (
                      <>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.distance_m?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.distance_per_min ?? '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.shi_distance?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.s7_distance?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.acchi_distance?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.dechi_distance?.toLocaleString() || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{row.metrics?.speed_max ?? '-'}</td>
                      </>
                    )}
                    {isMatch ? (
                      <>
                        <td className="p-2 text-right">
                          {canEdit ? (
                            <Input
                              type="number" min="0" max={event?.match_available_minutes || 200}
                              value={(() => { const v = getFieldValue(row, 'match_minutes'); return v == null ? '' : String(v); })()}
                              onChange={(e) => handleUpdateMatchMinutes(row.id, 'match_minutes', e.target.value)}
                              className={`w-16 h-7 text-center bg-zinc-950 border-zinc-700 text-zinc-100 font-mono ${drafts[row.id] && Object.prototype.hasOwnProperty.call(drafts[row.id], 'match_minutes') ? 'ring-1 ring-amber-500/60' : ''}`}
                              data-testid={`match-min-${row.id}`}
                            />
                          ) : (
                            <span className="text-zinc-300 font-mono">{row.match_minutes == null ? '—' : row.match_minutes}</span>
                          )}
                        </td>
                        <td className="p-2 text-center">
                          {canEdit ? (
                            <input
                              type="checkbox"
                              checked={getFieldValue(row, 'post_match_work') || false}
                              onChange={(e) => handleUpdateMatchMinutes(row.id, 'post_match_work', e.target.checked)}
                              className="rounded border-zinc-700"
                              data-testid={`post-work-${row.id}`}
                            />
                          ) : (
                            <span className="text-zinc-400">{row.post_match_work ? 'Yes' : '-'}</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {canEdit && (getFieldValue(row, 'post_match_work') || false) ? (
                            <Input
                              type="number" min="0" max="120"
                              value={(() => { const v = getFieldValue(row, 'post_match_minutes'); return v == null ? '' : String(v); })()}
                              onChange={(e) => handleUpdateMatchMinutes(row.id, 'post_match_minutes', e.target.value)}
                              className={`w-16 h-7 text-center bg-zinc-950 border-zinc-700 text-zinc-100 font-mono ${drafts[row.id] && Object.prototype.hasOwnProperty.call(drafts[row.id], 'post_match_minutes') ? 'ring-1 ring-amber-500/60' : ''}`}
                              data-testid={`post-min-${row.id}`}
                            />
                          ) : (
                            <span className="text-zinc-400 font-mono">{(getFieldValue(row, 'post_match_work') || false) ? (() => { const v = getFieldValue(row, 'post_match_minutes'); return v == null ? '—' : v; })() : '-'}</span>
                          )}
                        </td>
                      </>
                    ) : isTraining ? (
                      <td className="p-2 text-right text-zinc-300" data-cell="minutes" data-label={language === 'IT' ? 'Minuti individuali' : 'Individual minutes'}>
                        <TrainingMinutesCell row={row} context={trainingContext} draft={drafts[row.id]} language={language}
                          canEdit={canEdit} disabled={savingDrafts} onChange={handleUpdateTrainingMinutes} />
                      </td>
                    ) : (
                      <td className="p-2 text-right text-zinc-300">{row.metrics?.session_minutes || '-'}</td>
                    )}
                    <td className="p-2 text-center" data-label={isTraining ? 'RPE' : undefined}>
                      {canEdit ? (
                        <Input
                          type="number"
                          min="0"
                          max="10"
                          value={(() => { const v = getFieldValue(row, 'rpe'); return (v == null || (!isTraining && v === 0)) ? '' : v; })()}
                          onChange={(e) => handleUpdateRPE(row.id, e.target.value)}
                          disabled={isTraining && savingDrafts}
                          className={`w-14 h-7 text-center bg-zinc-950 border-zinc-700 text-amber-400 font-mono ${drafts[row.id] && Object.prototype.hasOwnProperty.call(drafts[row.id], 'rpe') ? 'ring-1 ring-amber-500/60' : ''}`}
                          data-testid={`rpe-input-${row.id}`}
                        />
                      ) : (
                        <span className="text-amber-400 font-mono">{row.rpe ?? '-'}</span>
                      )}
                    </td>
                    <td className="p-2 text-right text-emerald-400 font-mono" data-label={isTraining ? (language === 'IT' ? 'Carico' : 'Load') : undefined}
                      data-testid={`athlete-load-${row.id}`} title={isTraining ? `${language === 'IT' ? 'Base del carico' : 'Load basis'}: ${event.load_model === 'basketball_active_passive' ? event.adjusted_load_minutes ?? '—' : row.metrics?.session_minutes ?? '—'} min` : undefined}>
                      {row.training_load != null ? row.training_load.toLocaleString() : '-'}
                    </td>
                    <td className="p-2 text-center" data-label={isTraining ? (language === 'IT' ? 'Stato' : 'Status') : undefined}
                      data-testid={`athlete-rpe-status-${row.id}`} title={row.rpe_status}>
                      {row.rpe_status === 'SUBMITTED' ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" />
                      ) : row.rpe_status === 'EXPIRED' ? (
                        <AlertTriangle className="w-4 h-4 text-rose-400 mx-auto" />
                      ) : (
                        <Clock className="w-4 h-4 text-amber-400 mx-auto" />
                      )}
                    </td>
                    {canEdit && (
                      <td className="p-2 text-center">
                        <Button
                          size="sm" variant="ghost"
                          onClick={() => handleRemoveAthlete(row)}
                          disabled={removingId === row.id}
                          className="h-8 w-8 p-0 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                          title={language === 'IT' ? 'Rimuovi atleta' : 'Remove athlete'}
                          data-testid={`remove-athlete-${row.athlete_id}`}
                        >
                          {removingId === row.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <DialogFooter>
          <Button onClick={requestClose} className="bg-zinc-800 text-zinc-100 hover:bg-zinc-700" data-testid="event-detail-close-btn">
            {language === 'IT' ? 'Chiudi' : 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Delete Event Confirmation (p056) */}
      {showDeleteEvent && (
        <Dialog open={true} onOpenChange={(o) => { if (!o && !deletingEvent) setShowDeleteEvent(false); }}>
          <DialogContent className="bg-zinc-900 border-rose-500/30 sm:max-w-md" data-testid="delete-event-dialog">
            <DialogHeader>
              <DialogTitle className="text-zinc-100 flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-rose-400" />
                {language === 'IT' ? 'Eliminare questo evento?' : 'Delete this event?'}
              </DialogTitle>
            </DialogHeader>
            <div className="text-sm text-zinc-400 space-y-2">
              {hasSubmissions ? (
                <p className="text-amber-300" data-testid="delete-event-strong-warning">
                  {language === 'IT'
                    ? "Questo evento contiene RPE/carichi già registrati. Eliminando l'evento, questi dati non saranno più inclusi nelle analisi. Continuare?"
                    : "This event has submitted RPE / load data. Deleting it will remove those records from load analytics. Continue?"}
                </p>
              ) : (
                <p data-testid="delete-event-soft-warning">
                  {language === 'IT'
                    ? "Eliminare questo evento? L'operazione rimuoverà l'evento dal monitoraggio dei carichi."
                    : 'Delete this event? It will be removed from load monitoring.'}
                </p>
              )}
              <div className="rounded border border-zinc-800 bg-zinc-950/60 p-2 text-xs">
                <div className="text-zinc-500">{event.team_code} · {event.date}</div>
                <div className="text-zinc-200">{event.title}</div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                className="border-zinc-700"
                onClick={() => setShowDeleteEvent(false)}
                disabled={deletingEvent}
                data-testid="delete-event-cancel"
              >
                {language === 'IT' ? 'Annulla' : 'Cancel'}
              </Button>
              <Button
                onClick={handleDeleteEvent}
                disabled={deletingEvent}
                className="bg-rose-600 text-white hover:bg-rose-500"
                data-testid="delete-event-confirm"
              >
                {deletingEvent ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Trash2 className="w-4 h-4 mr-1.5" />}
                {language === 'IT' ? 'Elimina evento' : 'Delete event'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {pickerOpen && (
        <AddAthletePicker
          open={pickerOpen}
          onClose={() => { setPickerOpen(false); setPickerSelected([]); setPickerSearch(''); }}
          language={language}
          athletes={filteredAthletes}
          totalAvailable={availableAthletes.length}
          search={pickerSearch}
          onSearch={setPickerSearch}
          selected={pickerSelected}
          onSelect={setPickerSelected}
          onConfirm={handleAddAthletes}
          adding={adding}
        />
      )}
    </Dialog>
  );
};

const AddAthletePicker = ({ open, onClose, language, athletes, totalAvailable, search, onSearch, selected, onSelect, onConfirm, adding }) => {
  const t = (en, it) => language === 'IT' ? it : en;
  const toggle = (id) => {
    onSelect(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };
  const toggleAll = () => {
    const visibleIds = athletes.map(a => a.id);
    const allSelected = visibleIds.every(id => selected.includes(id));
    onSelect(allSelected ? selected.filter(id => !visibleIds.includes(id)) : [...new Set([...selected, ...visibleIds])]);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg max-h-[90vh] flex flex-col" data-testid="add-athlete-picker">
        <DialogHeader>
          <DialogTitle className="text-zinc-100">
            {t('Add athletes to event', 'Aggiungi atleti alla seduta')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2 flex-1 min-h-0 flex flex-col">
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder={t('Search by name or position...', 'Cerca per nome o ruolo...')}
            className="bg-zinc-950 border-zinc-700 text-zinc-100 min-h-[44px]"
            data-testid="add-athlete-search"
          />
          <div className="flex items-center justify-between text-xs text-zinc-500">
            <span>{selected.length} {t('selected', 'selezionati')} / {athletes.length} {t('shown', 'mostrati')} ({totalAvailable} {t('available', 'disponibili')})</span>
            {athletes.length > 0 && (
              <button className="text-amber-400 hover:text-amber-300" onClick={toggleAll} data-testid="picker-toggle-all">
                {athletes.every(a => selected.includes(a.id)) ? t('Clear all', 'Deseleziona tutti') : t('Select all filtered', 'Seleziona tutti')}
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto space-y-1 min-h-0 border border-zinc-800 rounded p-1 bg-zinc-950">
            {athletes.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-6">
                {totalAvailable === 0
                  ? t('No available athletes on this team', 'Nessun atleta disponibile in questa squadra')
                  : t('No results for this search', 'Nessun risultato')}
              </div>
            ) : athletes.map(a => (
              <label key={a.id}
                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-zinc-800 min-h-[44px] ${selected.includes(a.id) ? 'bg-emerald-500/10 border border-emerald-500/30' : 'border border-transparent'}`}
                data-testid={`picker-athlete-${a.id}`}>
                <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} className="rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-200 truncate">{a.first_name} {a.last_name}</div>
                  {a.position && <div className="text-[10px] text-zinc-500 uppercase">{a.position}</div>}
                </div>
              </label>
            ))}
          </div>
        </div>
        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={adding}
            className="border-zinc-700 text-zinc-200 hover:bg-zinc-800">
            {t('Cancel', 'Annulla')}
          </Button>
          <Button onClick={onConfirm}
            disabled={adding || selected.length === 0}
            className="bg-emerald-500 hover:bg-emerald-600 text-black"
            data-testid="picker-confirm-btn">
            {adding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            {t(`Add ${selected.length} athlete${selected.length !== 1 ? 's' : ''}`, `Aggiungi ${selected.length} atleti`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StaffGPSPage;
